import { useState, useMemo, useEffect } from 'react';
import { useAuthStore } from '../../store/authStore';
import { useHorarioAtual } from '../../api/hooks/useHorarios';
import { useStatusDia } from '../../api/hooks/useChamadas';
import { useProfessorTurmas } from '../../api/hooks/useProfessorTurmas';
import { useHojeReferencia } from '../../api/hooks/useSimClock';
import { api } from '../../api/client';
import type { TurmaDetalheData } from './TurmaDetalheSheet';
import type { DiaSemana } from '../../types/models';

export const DIAS_GRADE: { key: DiaSemana; label: string }[] = [
  { key: 'SEGUNDA', label: 'Segunda' },
  { key: 'TERCA', label: 'Terça' },
  { key: 'QUARTA', label: 'Quarta' },
  { key: 'QUINTA', label: 'Quinta' },
  { key: 'SEXTA', label: 'Sexta' },
  { key: 'SABADO', label: 'Sábado' },
];

export const TURNO_LABEL: Record<string, string> = {
  MANHA: 'Manhã',
  TARDE: 'Tarde',
  NOITE: 'Noite',
  INTEGRAL: 'Integral',
};

export function useMinhasTurmasProfessor() {
  const user = useAuthStore((s) => s.user);
  const escolaId = useAuthStore((s) => s.escolaId);
  const { hojeStr, isSimulation } = useHojeReferencia();
  const dataHoje = hojeStr || new Date().toISOString().split('T')[0];
  const { horariosHoje, diaSemana } = useHorarioAtual(
    user?.id,
    isSimulation ? dataHoje : undefined,
  );
  const { chamadasDia } = useStatusDia(user?.id, dataHoje);

  const { todosHorarios, horariosPorDia, isLoading } = useProfessorTurmas(user?.id);

  const turmas = useMemo(() => {
    const map = new Map<string, TurmaDetalheData & { diasLecionados: Set<string> }>();
    todosHorarios.forEach((h) => {
      if (!h.turmaId || !h.turma || h.tipo !== 'AULA') return;
      const ex = map.get(h.turmaId);
      if (ex) {
        ex.aulasSemana++;
        if (h.diaSemana) ex.diasLecionados.add(h.diaSemana);
        if (h.materia?.nome) ex.materias.add(h.materia.nome);
      } else {
        const s = new Set<string>();
        const m = new Set<string>();
        if (h.diaSemana) s.add(h.diaSemana);
        if (h.materia?.nome) m.add(h.materia.nome);
        map.set(h.turmaId, {
          id: h.turmaId,
          nome: h.turma.nome || '',
          serie: h.turma.serie || '',
          turno: h.turma.turnoOperacional?.nome || h.turma.turno || '',
          sala: h.turma.sala || '',
          aulasHoje: 0,
          aulasSemana: 1,
          totalAlunos: 0,
          chamadaHojeFeita: false,
          chamadaHojePres: 0,
          chamadaHojeTotal: 0,
          diasLecionados: s,
          materias: m,
        });
      }
    });
    horariosHoje.forEach((h) => {
      if (h.turmaId && h.tipo === 'AULA' && map.has(h.turmaId)) {
        map.get(h.turmaId)!.aulasHoje++;
      }
    });
    chamadasDia.forEach((c) => {
      if (c.turmaId && c.status === 'CONCLUIDA' && map.has(c.turmaId)) {
        const t = map.get(c.turmaId)!;
        t.chamadaHojeFeita = true;
        t.chamadaHojePres += c.totalPresentes || 0;
        t.chamadaHojeTotal += c.totalAlunos || 0;
      }
    });
    return Array.from(map.values()).sort((a, b) => {
      const ap = a.aulasHoje > 0 && !a.chamadaHojeFeita;
      const bp = b.aulasHoje > 0 && !b.chamadaHojeFeita;
      if (ap && !bp) return -1;
      if (!ap && bp) return 1;
      if (a.aulasHoje > 0 && b.aulasHoje === 0) return -1;
      if (a.aulasHoje === 0 && b.aulasHoje > 0) return 1;
      return a.nome.localeCompare(b.nome);
    });
  }, [todosHorarios, horariosHoje, chamadasDia]);

  const [contagemAlunos, setContagemAlunos] = useState<Record<string, number>>({});
  const turmaIdsKey = turmas.map((t) => t.id).join(',');

  useEffect(() => {
    if (!escolaId || turmas.length === 0) {
      setContagemAlunos({});
      return;
    }
    let cancelled = false;
    Promise.all(
      turmas.map(async (t) => {
        try {
          const { data } = await api.get<{ alunos?: unknown[] }>(
            `/escolas/${escolaId}/chamadas/turma/${t.id}/alunos`,
          );
          return { id: t.id, count: data.alunos?.length ?? 0 };
        } catch {
          return { id: t.id, count: 0 };
        }
      }),
    ).then((rows) => {
      if (cancelled) return;
      const next: Record<string, number> = {};
      rows.forEach((r) => {
        next[r.id] = r.count;
      });
      setContagemAlunos(next);
    });
    return () => {
      cancelled = true;
    };
  }, [escolaId, turmaIdsKey, turmas.length]);

  const turmasComContagem = useMemo(
    () =>
      turmas.map((t) => ({
        ...t,
        totalAlunos: contagemAlunos[t.id] ?? t.totalAlunos,
      })),
    [turmas, contagemAlunos],
  );

  const pendentesChamada = turmasComContagem.filter(
    (t) => t.aulasHoje > 0 && !t.chamadaHojeFeita,
  ).length;

  return {
    diaSemana,
    horariosPorDia,
    isLoading,
    turmasComContagem,
    pendentesChamada,
    contagemAlunos,
  };
}

export function buildGradeTurma(
  turmaId: string,
  horariosPorDia: ReturnType<typeof useProfessorTurmas>['horariosPorDia'],
) {
  return DIAS_GRADE.map((dia) => ({
    ...dia,
    aulas: (horariosPorDia[dia.key]?.aulas || []).filter(
      (h) => h.turmaId === turmaId && h.tipo === 'AULA',
    ),
  })).filter((d) => d.aulas.length > 0);
}
